/* =============================================================
   RONCASA — SCHOOLS NEAR DISTRICT/TOWN (full standalone page)
   Deliberately separate from district-town-hub.js (not a shared/extended
   file): this page's map has different behavior the Hub map never needed —
   clicking a school draws its real MOE P1 registration-priority bands
   (1km AND 2km, both real distance tiers, not decorative) and drives a real
   school detail panel + full sorted list, rather than category-toggle chips.

   3 Aug 2026 revision (Ronnie feedback after first live test):
   - Removed the hard maxBounds/maxBoundsViscosity lock the Hub map uses —
     this page is meant for exploring the surrounding area, so a hard
     pan-lock at a tight box felt wrong here on purpose, unlike the Hub's
     curated amenity map.
   - Added the 2km band alongside the 1km one (both real MOE priority tiers).
   - Added preschools (separate `type=preschool` API call — not in the
     `schools` BQ table/API type at all), with real ECDA vacancy-by-level/
     SPARK/food/transport data in the detail panel.
   - Level-based pin colour-coding, configurable from the theme editor
     (schools-full-listing.liquid's colour-picker settings, read here as CSS
     custom properties — not hardcoded hex).

   4 Aug 2026 revision (Ronnie feedback — list too long, icon contrast):
   - Added filter TABS by school type (All/Preschool/Primary/Secondary/
     College/Private) instead of one long mixed-distance list — mirrors the
     Hub map's own category-chip pattern (layersByCat), just applied to
     school level instead of amenity type.
   - Pin icon now gets a dark drop-shadow outline so it stays visible
     regardless of which background colour is picked in the theme editor —
     a light user-chosen colour was making the white icon strokes
     disappear into it.

   Reads #sch-area-data (center/bounds only — same shape as the Hub's
   #dth-area-data, written by schools-full-listing.liquid from the SAME
   `dth.map_config` metafield the Hub page already carries).
   ============================================================= */
(function () {
  'use strict';

  var mapEl = document.getElementById('sch-leaflet-map');
  var areaDataEl = document.getElementById('sch-area-data');
  if (!mapEl || !areaDataEl || typeof L === 'undefined') return;

  var AREA;
  try { AREA = JSON.parse(areaDataEl.textContent); } catch (e) { console.warn('[roncasa] sch-area-data JSON parse failed', e); return; }

  var LOCATION_API_BASE = 'https://api.roncasa.co/nearby';
  var ICON_SVG = {
    preschool: '<svg viewBox="0 0 16 16"><circle cx="8" cy="5" r="2.4"/><path d="M4 13c0-2.5 1.8-4 4-4s4 1.5 4 4"/></svg>',
    primary:   '<svg viewBox="0 0 16 16"><polygon points="8,3 14,6 8,9 2,6"/><line x1="8" y1="9" x2="8" y2="13"/><line x1="4" y1="7" x2="4" y2="10"/></svg>',
    secondary: '<svg viewBox="0 0 16 16"><polygon points="8,3 14,6 8,9 2,6"/><line x1="8" y1="9" x2="8" y2="13"/><line x1="4" y1="7" x2="4" y2="10"/></svg>',
    college:   '<svg viewBox="0 0 16 16"><rect x="3" y="6" width="10" height="7" rx="1"/><path d="M2 6l6-3 6 3-6 3z"/></svg>',
    private:   '<svg viewBox="0 0 16 16"><polygon points="8,3 14,6 8,9 2,6"/><line x1="8" y1="9" x2="8" y2="13"/><line x1="4" y1="7" x2="4" y2="10"/></svg>'
  };
  var BUCKET_LABEL = { preschool: 'Preschool', primary: 'Primary', secondary: 'Secondary', college: 'JC/Poly/Uni', private: 'Private' };
  var BUCKET_ORDER = ['preschool', 'primary', 'secondary', 'college', 'private'];
  var PRIORITY_RADIUS_M = 1000; // MOE P1 highest-priority band
  var STANDARD_RADIUS_M = 2000; // MOE P1 second-priority band (this API's own scoping cap too)

  // P1 ballot trend (9 Aug 2026) — s.p1_ballot_history is already on every
  // PRIMARY/MIXED school record served by /nearby (same sg_schools KV blob
  // the district/town hub reads), just not consumed on this page until now.
  // Reuses window.RoncasaP1Trend.render() from snippets/p1-trend.liquid —
  // same component, not reimplemented here.
  var PHASE_LABEL = { '1':'Phase 1','2A':'Phase 2A','2A1':'Phase 2A(1)','2A2':'Phase 2A(2)','2B':'Phase 2B','2C':'Phase 2C','2CS':'Phase 2C(S)' };
  var TIER_LABEL  = { 'SC_WITHIN_1KM':'SC, within 1km','SC_1KM_2KM':'SC, between 1-2km','SC_OUTSIDE_2KM':'SC, outside 2km',
                       'PR_WITHIN_1KM':'PR, within 1km','PR_1KM_2KM':'PR, between 1-2km','PR_OUTSIDE_2KM':'PR, outside 2km' };

  function p1BallotSectionHtml(s) {
    if (!s.p1_ballot_history || !s.p1_ballot_history.length) return '';
    var byPhase = {};
    s.p1_ballot_history.forEach(function (r) { (byPhase[r.phase] = byPhase[r.phase] || []).push(r); });
    var phases = Object.keys(byPhase).sort();
    return '<div class="sch-p1-section"><div class="sch-p1-hdr">Primary 1 ballot history — MOE official + third-party archive</div>' +
      phases.map(function (ph) {
        return '<div class="sch-p1-row"><span class="sch-p1-phase">' + (PHASE_LABEL[ph] || ph) + '</span>' +
          '<div class="rc-p1t-slot" data-p1-phase="' + ph + '"></div></div>';
      }).join('') + '</div>';
  }

  function renderP1Trends(s, root) {
    if (!window.RoncasaP1Trend || !s.p1_ballot_history) return;
    var byPhase = {};
    s.p1_ballot_history.forEach(function (r) { (byPhase[r.phase] = byPhase[r.phase] || []).push(r); });
    root.querySelectorAll('[data-p1-phase]').forEach(function (slot) {
      RoncasaP1Trend.render(slot, byPhase[slot.dataset.p1Phase] || [], { tierLabel: TIER_LABEL });
    });
  }

  // ── Level -> colour bucket. Real distinct `schools.level` values (confirmed
  // live via BQ, 3 Aug 2026): PRIMARY, SECONDARY, PRIVATE, JC, UNIVERSITY,
  // POLYTECHNIC, ITE, MIXED, ARTS. Preschools come from a separate API call
  // (type=preschool) and are tagged 'preschool' here, not present in `schools`
  // at all.
  function bucketFor(levelRaw) {
    var lvl = (levelRaw || '').toString().toUpperCase();
    if (lvl === 'PRESCHOOL') return 'preschool';
    if (lvl === 'PRIMARY' || lvl === 'MIXED') return 'primary';
    if (lvl === 'SECONDARY') return 'secondary';
    if (lvl === 'JC' || lvl === 'UNIVERSITY' || lvl === 'POLYTECHNIC' || lvl === 'ITE' || lvl === 'ARTS') return 'college';
    if (lvl === 'PRIVATE') return 'private';
    return 'primary';
  }
  function colorVarFor(bucket) { return 'var(--sch-color-' + bucket + ')'; }

  var map = L.map(mapEl, {
    center: AREA.center, zoom: AREA.zoom, minZoom: AREA.minZoom, maxZoom: AREA.maxZoom,
    scrollWheelZoom: false
    // No maxBounds/maxBoundsViscosity here on purpose — see file header.
  });

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap', maxZoom: 19
  }).addTo(map);

  // Ctrl/Cmd + scroll to zoom — same convention as the Hub map, plain scroll
  // keeps scrolling the page instead of trapping it.
  var mapContainerEl = map.getContainer();
  var zoomHintEl = document.createElement('div');
  zoomHintEl.className = 'sch-map-zoom-hint';
  zoomHintEl.textContent = 'Ctrl + scroll to zoom';
  mapContainerEl.appendChild(zoomHintEl);
  mapContainerEl.addEventListener('wheel', function (e) {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (e.deltaY < 0) map.zoomIn(); else map.zoomOut();
      zoomHintEl.style.opacity = '0';
    }
  }, { passive: false });
  mapContainerEl.addEventListener('mouseleave', function () { zoomHintEl.style.opacity = ''; });

  function clusterIconFn(cluster) {
    var n = cluster.getChildCount();
    var tier = n < 8 ? 'sm' : n < 20 ? 'md' : 'lg';
    return L.divIcon({ html: '<div class="dth-cluster ' + tier + '">' + n + '</div>', className: '', iconSize: null });
  }

  // One cluster group PER bucket (mirrors the Hub map's layersByCat pattern)
  // so a filter tab can show/hide a whole level at once, not just the list.
  var layersByBucket = {};
  BUCKET_ORDER.forEach(function (b) {
    layersByBucket[b] = (typeof L.markerClusterGroup === 'function')
      ? L.markerClusterGroup({ iconCreateFunction: clusterIconFn, showCoverageOnHover: false, spiderfyOnMaxZoom: true, maxClusterRadius: 45 })
      : L.layerGroup();
    layersByBucket[b].addTo(map);
  });

  var priorityCircle = null, standardCircle = null;
  var schools = []; // {name, level, bucket, postal, zone, autonomous, sap, gifted, ip, lat, lng, _dist, marker, listRow}
  var activeIdx = -1;
  var activeFilter = 'all';

  var listEl = document.getElementById('sch-list');
  var detailEl = document.getElementById('sch-detail');
  var tabsEl = document.getElementById('sch-tabs');

  function fmtDist(m) {
    return m < 1000 ? Math.round(m) + 'm' : (m / 1000).toFixed(1) + 'km';
  }

  function levelLabel(s) {
    if (s.bucket === 'preschool') return 'Preschool';
    var lvl = (s.level || 'School').toString();
    return lvl.charAt(0).toUpperCase() + lvl.slice(1).toLowerCase();
  }

  function tagsFor(s) {
    var tags = [];
    if (s.bucket === 'preschool') {
      if (s.spark_certified) tags.push('SPARK Certified');
      if (s.has_infant) tags.push('Infant Care');
      if (s.food_offered) tags.push('Food Provided');
      if (s.transport) tags.push('Transport');
      return tags;
    }
    if (s.sap) tags.push('SAP');
    if (s.gifted) tags.push('GEP');
    if (s.ip) tags.push('IP');
    if (s.autonomous) tags.push('Autonomous');
    return tags;
  }

  var VACANCY_ORDER = [['infant', 'Infant'], ['playgroup', 'Playgroup'], ['n1', 'Nursery 1'], ['n2', 'Nursery 2'], ['k1', 'Kindergarten 1'], ['k2', 'Kindergarten 2']];
  function vacancyRowsHtml(s) {
    if (!s.vacancy) return '';
    var rows = VACANCY_ORDER.filter(function (pair) { return s.vacancy[pair[0]]; }).map(function (pair) {
      var status = s.vacancy[pair[0]];
      var cls = status === 'Full' ? 'vac-full' : (status === 'Limited' ? '' : 'vac-avail');
      return '<div class="row"><span>' + pair[1] + '</span><b class="' + cls + '">' + status + '</b></div>';
    }).join('');
    return rows ? '<div class="vacancy-note">Vacancy, ECDA listing</div>' + rows : '';
  }

  function selectSchool(i) {
    if (activeIdx >= 0 && schools[activeIdx]) {
      schools[activeIdx].marker._icon && schools[activeIdx].marker._icon.classList.remove('on');
      schools[activeIdx].listRow && schools[activeIdx].listRow.classList.remove('on');
    }
    activeIdx = i;
    var s = schools[i];
    if (!s) return;

    if (priorityCircle) map.removeLayer(priorityCircle);
    if (standardCircle) map.removeLayer(standardCircle);
    standardCircle = L.circle([s.lat, s.lng], {
      radius: STANDARD_RADIUS_M, color: '#3D6FA8', weight: 2, fillColor: '#3D6FA8', fillOpacity: 0.05, dashArray: '3,6'
    }).addTo(map);
    priorityCircle = L.circle([s.lat, s.lng], {
      radius: PRIORITY_RADIUS_M, color: '#772C2C', weight: 1.5, fillColor: '#772C2C', fillOpacity: 0.07, dashArray: '4,3'
    }).addTo(map);
    // Keep the smaller 1km ring drawn on top of the 2km one.
    priorityCircle.bringToFront();

    s.marker._icon && s.marker._icon.classList.add('on');
    s.listRow && s.listRow.classList.add('on');
    s.listRow && s.listRow.scrollIntoView({ block: 'nearest' });

    var tags = tagsFor(s);
    var isPreschool = s.bucket === 'preschool';
    detailEl.className = 'sch-detail on';
    detailEl.innerHTML =
      '<button class="sch-clear-btn" type="button" aria-label="Clear selection" title="Clear selection">&times;</button>' +
      '<div class="name">' + s.name + '</div>' +
      '<div class="lvl">' + levelLabel(s) + (s.zone ? ' &middot; ' + s.zone + ' Zone' : '') + '</div>' +
      (tags.length ? '<div class="tags">' + tags.map(function (t) { return '<span class="sch-tag">' + t + '</span>'; }).join('') + '</div>' : '') +
      '<div class="row"><span>Distance from area centre</span><b>' + fmtDist(s._dist) + '</b></div>' +
      (s.postal ? '<div class="row"><span>Postal code</span><b>' + s.postal + '</b></div>' : '') +
      (isPreschool
        ? (s.operating_hours ? '<div class="row stack"><span>Hours</span><b>' + s.operating_hours + '</b></div>' : '') + vacancyRowsHtml(s)
        : '<div class="row"><span>Within 1km (highest priority)</span><b class="ring-1">shown on map</b></div>' +
          '<div class="row"><span>Within 2km (next priority)</span><b class="ring-2">shown on map</b></div>') +
      (isPreschool ? '' : p1BallotSectionHtml(s));

    var clearBtn = detailEl.querySelector('.sch-clear-btn');
    if (clearBtn) clearBtn.addEventListener('click', function (e) { e.stopPropagation(); clearSelection(); });
    renderP1Trends(s, detailEl);
  }

  function clearSelection() {
    if (activeIdx >= 0 && schools[activeIdx]) {
      schools[activeIdx].marker._icon && schools[activeIdx].marker._icon.classList.remove('on');
      schools[activeIdx].listRow && schools[activeIdx].listRow.classList.remove('on');
    }
    activeIdx = -1;
    if (priorityCircle) { map.removeLayer(priorityCircle); priorityCircle = null; }
    if (standardCircle) { map.removeLayer(standardCircle); standardCircle = null; }
    detailEl.className = 'sch-detail';
    detailEl.innerHTML = '';
  }

  function applyFilter() {
    BUCKET_ORDER.forEach(function (b) {
      var show = activeFilter === 'all' || activeFilter === b;
      if (show) { if (!map.hasLayer(layersByBucket[b])) map.addLayer(layersByBucket[b]); }
      else { if (map.hasLayer(layersByBucket[b])) map.removeLayer(layersByBucket[b]); }
    });
    renderList();
  }

  function renderTabs() {
    if (!tabsEl) return;
    var counts = { all: schools.length };
    BUCKET_ORDER.forEach(function (b) { counts[b] = 0; });
    schools.forEach(function (s) { counts[s.bucket] = (counts[s.bucket] || 0) + 1; });

    var html = '<button class="sch-tab on" data-filter="all">All <span>' + counts.all + '</span></button>';
    BUCKET_ORDER.forEach(function (b) {
      if (!counts[b]) return;
      html += '<button class="sch-tab" data-filter="' + b + '" style="--tab-sw:' + colorVarFor(b) + '">' + BUCKET_LABEL[b] + ' <span>' + counts[b] + '</span></button>';
    });
    tabsEl.innerHTML = html;
    tabsEl.querySelectorAll('.sch-tab').forEach(function (btn) {
      btn.addEventListener('click', function () {
        tabsEl.querySelectorAll('.sch-tab').forEach(function (b) { b.classList.remove('on'); });
        btn.classList.add('on');
        activeFilter = btn.dataset.filter;
        // 9 Aug 2026 (Ronnie feedback): switching category while a school's
        // detail card is open left it open and stale (still showing the
        // previous category's selection) — auto-close it here so the
        // transition feels like one seamless state change, not two.
        clearSelection();
        applyFilter();
      });
    });
  }

  function renderList() {
    if (!listEl) return;
    var visible = schools.filter(function (s) { return activeFilter === 'all' || s.bucket === activeFilter; });
    if (!visible.length) {
      listEl.innerHTML = '<div class="sch-list-empty">No schools in this category nearby.</div>';
      return;
    }
    listEl.innerHTML = '';
    visible.forEach(function (s) {
      var i = schools.indexOf(s);
      var row = document.createElement('button');
      row.className = 'sch-row';
      row.innerHTML = '<span class="badge" style="background:' + colorVarFor(s.bucket) + '">' + (ICON_SVG[s.bucket] || '') + '</span>' +
        '<span class="info"><span class="nm">' + s.name + '</span><br><span class="lvl">' + levelLabel(s) + '</span></span>' +
        '<span class="dist">' + fmtDist(s._dist) + '</span>';
      row.addEventListener('click', function () {
        map.flyTo([s.lat, s.lng], Math.max(AREA.maxZoom - 1, AREA.minZoom));
        selectSchool(i);
      });
      s.listRow = row;
      listEl.appendChild(row);
    });
  }

  function renderPins() {
    BUCKET_ORDER.forEach(function (b) { layersByBucket[b].clearLayers(); });
    schools.forEach(function (s, i) {
      var icon = L.divIcon({
        className: '',
        html: '<div class="dth-pin" style="background:' + colorVarFor(s.bucket) + '">' + (ICON_SVG[s.bucket] || '') + '</div>',
        iconSize: [24, 24], iconAnchor: [12, 12]
      });
      var marker = L.marker([s.lat, s.lng], { icon: icon }).bindPopup('<b>' + s.name + '</b><br>' + levelLabel(s));
      marker.on('click', function () { selectSchool(i); });
      marker.addTo(layersByBucket[s.bucket]);
      s.marker = marker;
    });
  }

  function fetchType(apiType, radius, limit) {
    var url = LOCATION_API_BASE + '?lat=' + AREA.center[0] + '&lng=' + AREA.center[1] + '&type=' + apiType + '&radius=' + radius + '&limit=' + limit;
    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error(apiType + ' lookup returned ' + res.status);
      return res.json();
    }).then(function (body) { return body.results || []; }).catch(function (err) {
      console.warn('[roncasa] ' + apiType + ' lookup failed: ' + err.message);
      return [];
    });
  }

  var PRESCHOOL_RADIUS_M = 2000; // matches the school fetch + the Worker's own hard cap — full real sample for the density calc below

  // Shared density/spacing calc — derived from the real geographic area
  // actually queried (a radiusM-radius circle around the area centre), not
  // just a raw count (Ronnie, 4 Aug 2026: "use the map size to actually tell
  // how family friendly the area is"). areaKm2 is the true area of that
  // circle; density is items per km²; avgSpacingM is the average distance
  // between neighbours you'd expect if that density were spread evenly
  // (sqrt(area/count)) — an honest average, not a literal nearest-neighbour
  // measurement, and scoped to this one radius-capped sample, not a claimed
  // island-wide ranking. Standardised so preschool and primary-school notes
  // read the same way (Ronnie, 4 Aug 2026: "so at least it's standardize").
  function buildDensitySummary(items, radiusM, noun, labelFor) {
    var radiusKm = radiusM / 1000;
    var areaKm2 = Math.PI * radiusKm * radiusKm;
    if (!items.length) return 'No ' + noun + ' found within ' + radiusKm.toFixed(1) + 'km.';
    var within500 = items.filter(function (s) { return s._dist <= 500; }).length;
    var density = items.length / areaKm2;
    var avgSpacingM = Math.round(Math.sqrt(areaKm2 / items.length) * 1000);
    return items.length + ' ' + noun + ' within ' + radiusKm.toFixed(1) + 'km (' +
      density.toFixed(1) + '/km², roughly one every ' + avgSpacingM + 'm on average' +
      (within500 ? ', ' + within500 + ' within 500m' : '') + ') — ' + labelFor(density) + '.';
  }

  function injectDensitySummary(noteId, sentence) {
    var noteEl = document.getElementById(noteId);
    if (!noteEl) return;
    var summaryEl = document.createElement('p');
    summaryEl.style.fontStyle = 'normal';
    summaryEl.style.color = 'var(--rc-ink-2)';
    summaryEl.style.margin = '0 0 10px';
    summaryEl.innerHTML = '<strong>' + sentence + '</strong>';
    noteEl.insertBefore(summaryEl, noteEl.firstChild);
  }

  // ── 2025 P1 comparison table (sortable) ──────────────────────────────
  // Mirrors school-page.js's own sortable-table pattern (.scp-table: click
  // a header to sort asc/desc, nulls always sort to the bottom regardless
  // of direction) — same interaction, namespaced .sch-cmp- here since this
  // is schools-page.js, not school-page.js. Built entirely from data
  // already on the page (each school's p1_ballot_history, from the same
  // /nearby fetch that populated the map) — no second network call.
  var COMPARE_PHASES = [
    { key: '1',   label: 'Phase 1' },
    { key: '2A',  label: 'Phase 2A' },
    { key: '2B',  label: 'Phase 2B' },
    { key: '2C',  label: 'Phase 2C' },
    { key: '2CS', label: 'Phase 2C(S)' }
  ];

  function admissionCell(school, phaseKey) {
    var hist = school.p1_ballot_history || [];
    var row = hist.filter(function (r) { return r.year === 2025 && r.phase === phaseKey; })[0];
    if (!row) return { display: '—', sortVal: null };
    if (!row.balloted) return { display: 'No ballot', sortVal: 100 };
    if (row.ballot_vacancies != null && row.ballot_applicants) {
      var pct = Math.round((row.ballot_vacancies / row.ballot_applicants) * 100);
      return { display: pct + '%', sortVal: pct };
    }
    return { display: '—', sortVal: null };  // balloted but MOE didn't disclose a number this phase
  }

  function buildCompareTable(allSchools) {
    var root = document.getElementById('sch-compare-root');
    if (!root) return;

    var rows = allSchools
      .filter(function (s) { return s.bucket === 'primary' && s.p1_ballot_history && s.p1_ballot_history.length; })
      .map(function (s) {
        var cells = {};
        var has2025 = false;
        COMPARE_PHASES.forEach(function (p) {
          cells[p.key] = admissionCell(s, p.key);
          if (cells[p.key].sortVal != null) has2025 = true;
        });
        return has2025 ? { name: s.name, dist: s._dist, cells: cells } : null;
      })
      .filter(Boolean);

    if (!rows.length) {
      root.innerHTML = '<div class="sch-list-empty">No 2025 P1 ballot data on file for schools in this area yet.</div>';
      return;
    }

    var sortKey = 'name', sortAsc = true;

    function draw() {
      var sorted = rows.slice().sort(function (a, b) {
        var av = sortKey === 'name' ? a.name.toLowerCase() : a.cells[sortKey].sortVal;
        var bv = sortKey === 'name' ? b.name.toLowerCase() : b.cells[sortKey].sortVal;
        if (av == null) return 1; if (bv == null) return -1;
        var cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortAsc ? cmp : -cmp;
      });
      var thead = '<thead><tr>' +
        '<th data-key="name" class="name' + (sortKey === 'name' ? ' on ' + (sortAsc ? 'asc' : 'desc') : '') + '">School</th>' +
        COMPARE_PHASES.map(function (p) {
          var on = p.key === sortKey;
          return '<th data-key="' + p.key + '" class="' + (on ? 'on ' + (sortAsc ? 'asc' : 'desc') : '') + '">' + p.label + '</th>';
        }).join('') + '</tr></thead>';
      var tbody = '<tbody>' + sorted.map(function (r) {
        return '<tr><td class="name">' + r.name + '</td>' +
          COMPARE_PHASES.map(function (p) { return '<td>' + r.cells[p.key].display + '</td>'; }).join('') +
          '</tr>';
      }).join('') + '</tbody>';
      root.innerHTML = '<div class="sch-cmp-table-wrap"><table class="sch-cmp-table">' + thead + tbody + '</table></div>' +
        '<div class="sch-compare-caveat">2025 P1 Registration Exercise, MOE official data (and third-party-archive-sourced years where noted elsewhere on this page). "No ballot" = every eligible applicant in that phase was offered a place.</div>';
      root.querySelectorAll('th').forEach(function (th) {
        th.addEventListener('click', function () {
          var key = th.dataset.key;
          if (key === sortKey) sortAsc = !sortAsc; else { sortKey = key; sortAsc = key === 'name'; }
          draw();
        });
      });
    }
    draw();
  }

  function fetchSchools() {
    Promise.all([
      fetchType('school', 2000, 30),
      fetchType('preschool', PRESCHOOL_RADIUS_M, 30)
    ]).then(function (results) {
      var real = results[0].map(function (r) { return Object.assign({}, r, { bucket: bucketFor(r.level) }); });
      var pre = results[1].map(function (r) { return Object.assign({}, r, { level: 'PRESCHOOL', bucket: 'preschool' }); });
      schools = real.concat(pre).filter(function (r) { return r.lat != null && r.lng != null && r.name; });
      schools.sort(function (a, b) { return a._dist - b._dist; });
      renderPins();
      renderTabs();
      applyFilter();
      var countEl = document.getElementById('sch-count-note');
      if (countEl) {
        countEl.textContent = schools.length
          ? schools.length + ' school' + (schools.length === 1 ? '' : 's') + ' found nearby · filter by type below, or click any for its 1km/2km registration bands'
          : 'No schools found nearby.';
      }

      // Preschool density — same standardised calc as the primary-school one
      // below (Ronnie, 4 Aug 2026: "so at least it's standardize").
      var preschools = schools.filter(function (s) { return s.bucket === 'preschool'; });
      var preschoolSentence = buildDensitySummary(preschools, PRESCHOOL_RADIUS_M, 'preschools scattered', function (density) {
        return density >= 5 ? 'exceptionally preschool-dense'
          : density >= 2.5 ? 'well covered for childcare'
          : density >= 1 ? 'reasonably served'
          : 'sparser than most areas for childcare';
      });
      var densityEl = document.getElementById('sch-preschool-density');
      if (densityEl) densityEl.textContent = preschoolSentence;
      if (preschools.length) injectDensitySummary('sch-preschool-note', preschoolSentence);

      // Primary school density — uses STANDARD_RADIUS_M, the real 2km MOE
      // P1 registration-priority band already drawn on the map, not an
      // arbitrary sample radius.
      var primarySchools = schools.filter(function (s) { return s.bucket === 'primary'; });
      var primarySentence = buildDensitySummary(primarySchools, STANDARD_RADIUS_M, 'primary schools', function (density) {
        return density >= 1.5 ? 'an unusually deep primary school cluster'
          : density >= 0.8 ? 'well served for primary school choice'
          : density >= 0.4 ? 'a reasonable spread of primary options'
          : 'thin primary school coverage — distance will matter more than choice here';
      });
      if (primarySchools.length) injectDensitySummary('sch-primary-note', primarySentence);

      buildCompareTable(schools);
    });
  }

  fetchSchools();
})();
